import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Callout, FormGroup, InputGroup, Spinner } from "@blueprintjs/core";

import { AppToaster, Code, RobotsNoindex, SsoSetupForm } from "@components";
import { SsoProviderSettings } from "@types";
import { confirmPrompt, withToaster } from "@utils";

import { getSsoTokenEmail, postSsoDeployment } from "./api";

const PageWrapper = styled.div`
  padding-top: var(--global-spacing-navbar-height);
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--indigo-gradient-245);
  color: var(--global-light-gray);
  justify-content: flex-start;
  min-height: 100vh;
`;

const FormWrapper = styled.div`
  padding-top: 20px;
  width: 500px;
`;

const MarginCallout = styled(Callout)`
  margin-bottom: 15px;
`;

export const PublicCreateSSODeployment: React.FC = () => {
  const [deploymentName, setDeploymentName] = useState<string>("");
  // token is valid iff emailDomain is not null and tokenValidityLoaded is true
  const [emailDomain, setEmailDomain] = useState<string | null>(null);
  const [deploymentCreated, setDeploymentCreated] = useState<boolean>(false);
  const [tokenValidityLoaded, setTokenValidityLoaded] =
    useState<boolean>(false);

  const queryParams = new URLSearchParams(useLocation().search);
  const token = queryParams.get("t");

  useEffect(() => {
    if (token) {
      getSsoTokenEmail(token)
        .then((resp) => {
          setEmailDomain(resp.email_domain);
          setTokenValidityLoaded(true);
        })
        .catch(() => {
          setTokenValidityLoaded(true);
          setEmailDomain(null);
        });
    } else {
      setTokenValidityLoaded(true);
      setEmailDomain(null);
    }
  }, [token]);

  const createSsoDeployment = (ssoSettings: SsoProviderSettings) => {
    confirmPrompt(
      `Are you sure you want to create deployment ${deploymentName} for email domain ${ssoSettings.email_domain}?`
    ).then(() =>
      withToaster(
        () =>
          postSsoDeployment({
            ...ssoSettings,
            token: token!, // this will only be called when token is non-null
            deployment_name: deploymentName,
          }).then(() => {
            AppToaster.show({
              message: "Auth provider updated.",
              intent: "success",
            });
            setDeploymentCreated(true);
          }),
        (err) => `Failed to create deployment: ${err.body?.error}`
      )()
    );
  };

  if (!tokenValidityLoaded) {
    return (
      <>
        <RobotsNoindex />
        <PageWrapper>
          <Spinner />
        </PageWrapper>
      </>
    );
  }

  return (
    <>
      <RobotsNoindex />
      <PageWrapper>
        {!deploymentCreated && emailDomain ? ( // implies the existence of valid token
          <FormWrapper>
            <h1>Set up new SSO Deployment</h1>
            <MarginCallout>
              For email domains{" "}
              <Code inline light>
                @{emailDomain}
              </Code>
            </MarginCallout>
            <FormGroup
              label="Deployment Name"
              helperText="The name to be used for the new deployment"
            >
              <InputGroup
                placeholder="required"
                required
                value={deploymentName}
                onChange={(e: React.FormEvent<HTMLElement>) => {
                  setDeploymentName((e.target as HTMLInputElement).value);
                }}
              />
            </FormGroup>

            <SsoSetupForm
              deploymentId={null}
              emailDomain={emailDomain}
              providerId={deploymentName}
              handleSaveSSO={createSsoDeployment}
              skipBaseUrlValidation
            />
          </FormWrapper>
        ) : !emailDomain ? (
          <>
            <h1>Invalid Link</h1>
            <p>
              Please request a new link from Semgrep Support to continue your
              SSO setup.
            </p>
          </>
        ) : (
          <>
            <h1>Deployment Created</h1>
            <p>
              Your deployment{" "}
              <Code inline light>
                {deploymentName}
              </Code>{" "}
              has been created. Please <Link to={"/login"}>log in</Link> to
              continue.
            </p>
          </>
        )}
      </PageWrapper>
    </>
  );
};
